import React, { ReactNode } from 'react';
import { Image, StyleSheet, View } from 'react-native';

import BetrLogo from '@/assets/icons/betr-small';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { Product } from '@/hooks/use-jurisdiction';

import { ds } from '../lib/design.system';
import { EntryConfirmationShell } from './EntryConfirmationShell';

export type EntryConfirmationProduct = Product.Pickem | Product.Sweeps | Product.Prediction;

export type EntryConfirmationRow = {
    label: string;
    value: string;
    valuePrefix?: ReactNode;
    emphasized?: boolean;
    labelIcon?: ReactNode;
    testID?: string;
};

type Props = {
    onClose: () => void;
    title: string;
    product: EntryConfirmationProduct;
    rows: EntryConfirmationRow[];
    buttons: ReactNode;
    banner?: ReactNode;
    animated?: boolean;
    visible?: boolean;
    onExitComplete?: () => void;
};

const PRODUCT_CONFIG: Record<EntryConfirmationProduct, { label: string }> = {
    [Product.Pickem]: { label: 'Picks' },
    [Product.Sweeps]: { label: 'Social Sportsbook' },
    [Product.Prediction]: { label: 'Predictions' },
};

export const EntryConfirmation = ({
    onClose,
    title,
    product,
    rows,
    buttons,
    banner,
    animated,
    visible,
    onExitComplete,
}: Props) => {
    const { label } = PRODUCT_CONFIG[product];

    return (
        <EntryConfirmationShell onClose={onClose} animated={animated} visible={visible} onExitComplete={onExitComplete}>
            <Box flex={1} paddingHorizontal="s40" justifyContent={'center'}>
                <Box gap="s32" mb={'s6'}>
                    <Box gap="s28" alignItems="center">
                        <Box alignItems="center">
                            <Image
                                source={require('@/assets/images/entry_confirmation_check.png')}
                                style={styles.checkIcon}
                                resizeMode="cover"
                            />
                            <Text variant="headlineLarge" textAlign="center" color={ds.colorToken.text.primary}>
                                {title}
                            </Text>
                        </Box>

                        <Box flexDirection="row" alignItems="center" gap="s16" width="100%">
                            <View style={styles.dividerFlex} />
                            <Box flexDirection="row" alignItems="center" gap="s4" style={styles.logoLockup}>
                                <BetrLogo width={16} height={16} />
                                <Text variant="titleMedium" color={ds.colorToken.text.primary}>
                                    {label}
                                </Text>
                            </Box>
                            <View style={styles.dividerFlex} />
                        </Box>

                        <Box width="100%" gap="s12">
                            {rows.map((row, index) => (
                                <React.Fragment key={`${row.label}-${index}`}>
                                    {index > 0 ? <View style={styles.dividerFull} /> : null}
                                    <Box
                                        flexDirection="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        testID={row.testID}
                                    >
                                        <Box flexDirection="row" alignItems="center" gap="s4">
                                            <Text
                                                variant={row.emphasized ? 'titleMedium' : 'bodyMedium'}
                                                color={ds.colorToken.text.primary}
                                                style={row.emphasized ? undefined : styles.labelDimmed}
                                            >
                                                {row.label}
                                            </Text>
                                            {row.labelIcon}
                                        </Box>
                                        <Box flexDirection="row" alignItems="center" gap="s4">
                                            {row.valuePrefix}
                                            <Text
                                                variant={row.emphasized ? 'headlineLarge' : 'titleMedium'}
                                                color={ds.colorToken.text.primary}
                                                textAlign="right"
                                            >
                                                {row.value}
                                            </Text>
                                        </Box>
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>

                    <Box gap="s16">
                        <Box flexDirection="row" alignItems="flex-start" gap="s12">
                            {buttons}
                        </Box>
                        {banner ? <Box>{banner}</Box> : null}
                    </Box>
                </Box>
            </Box>
        </EntryConfirmationShell>
    );
};

const styles = StyleSheet.create({
    dividerFlex: {
        flex: 1,
        height: 1,
        backgroundColor: ds.colorValue.border.strong,
    },
    dividerFull: {
        width: '100%',
        height: 1,
        backgroundColor: ds.colorValue.border.strong,
    },
    logoLockup: {
        opacity: 0.6,
    },
    labelDimmed: {
        opacity: 0.6,
    },
    checkIcon: {
        width: 120,
        height: 104,
        marginTop: 18,
    },
});
